import { SamplesBrand } from 'constants/brands';
import React from 'react';
import { Group, useBreakpoint } from '@landr/maestro';
import { Trans } from '@lingui/macro';
import Slice from 'components/Slice/Slice';
import { useBundleCatalog, useSamplesCatalog } from '@landr/pricing';
import { addCurrencyToPrice } from 'utils/price';
import { SectionHeader } from 'components/Header/Header';
import {
    BundleCatalogPlan,
    SampleCatalogPlan,
} from 'types/generated/subscriptionsApi';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { useFlag } from 'hooks/useFlag/useFlag';
import { FeatureFlag } from 'types/enums';
import NeedMoreSamples from './components/NeedMoreSamples';
import PricingCard from './components/PricingCard';
import { samplesPricing, studioPricing } from './content';

export const usePricingCards = () => {
    const { hasFetchedSubscription, hasStudioPlan, hasSamplesPlan } =
        useSubscriptionsAndAllowedFeaturesContext();
    const isMobile = useBreakpoint('md');

    const showSampleCard = !(hasSamplesPlan || hasStudioPlan);
    const showStudioCard = !hasStudioPlan;
    const showHeading = showSampleCard || showStudioCard;
    const showNeedMoreCredit = !showSampleCard;

    return {
        hasFetchedSubscription,
        showHeading,
        showSampleCard,
        showStudioCard,
        showNeedMoreCredit,
        isMobile,
    };
};

const DefaultHeading = () => {
    const isMobile = useBreakpoint('md');

    return (
        <SectionHeader
            size={isMobile ? 'xl' : 'xxxl'}
            mb={isMobile ? 'xxl' : 'jb'}
        >
            <Trans>Get {SamplesBrand} credits</Trans>
        </SectionHeader>
    );
};

export const _PricingCards = ({
    showHeading,
    showSampleCard,
    showStudioCard,
    showNeedMoreCredit,
    isMobile,
    isLoading,
    samplesMonthlyEquivalentPriceWithCurrency,
    studioMonthlyEquivalentPriceWithCurrency,
    isChromaticMS2Enabled,
    header: Header = DefaultHeading,
}: {
    showHeading: boolean;
    showSampleCard: boolean;
    showStudioCard: boolean;
    showNeedMoreCredit: boolean;
    isMobile: boolean;
    isLoading: boolean;
    samplesMonthlyEquivalentPriceWithCurrency: string;
    studioMonthlyEquivalentPriceWithCurrency: string;
    isChromaticMS2Enabled?: boolean;
    header?: () => JSX.Element;
}) => (
    <>
        {showHeading && <Header />}
        <Slice>
            <Group direction="vertical" gutter="jb">
                {showSampleCard && (
                    <Group.Item>
                        <PricingCard
                            isLoading={isLoading}
                            direction="row"
                            content={samplesPricing(
                                samplesMonthlyEquivalentPriceWithCurrency,
                                isChromaticMS2Enabled,
                            )}
                            isMobile={isMobile}
                        />
                    </Group.Item>
                )}
                {showStudioCard && (
                    <Group.Item>
                        <PricingCard
                            isLoading={isLoading}
                            direction="row-reverse"
                            content={studioPricing(
                                studioMonthlyEquivalentPriceWithCurrency,
                                isChromaticMS2Enabled,
                            )}
                            isMobile={isMobile}
                        />
                    </Group.Item>
                )}
                {showNeedMoreCredit && (
                    <Group.Item>
                        <NeedMoreSamples />
                    </Group.Item>
                )}
            </Group>
        </Slice>
    </>
);

const PricingCards = ({
    customHeader,
}: {
    customHeader?: () => JSX.Element;
}) => {
    const {
        hasFetchedSubscription,
        showHeading,
        showSampleCard,
        showStudioCard,
        showNeedMoreCredit,
        isMobile,
    } = usePricingCards();

    const { data: studioCatalog } = useBundleCatalog();
    const { data: samplesCatalog } = useSamplesCatalog();

    const sampleMonthlyEquivalentPriceYearly = samplesCatalog?.plans.find(
        (plan: SampleCatalogPlan) => plan?.frequency === 'Yearly',
    );

    const studioMonthlyEquivalentPriceYearly = studioCatalog?.plans.find(
        (plan: BundleCatalogPlan) => plan?.frequency === 'Yearly',
    );

    const samplesMonthlyEquivalentPriceWithCurrency = addCurrencyToPrice(
        samplesCatalog ? samplesCatalog?.currency : undefined,
        sampleMonthlyEquivalentPriceYearly
            ? sampleMonthlyEquivalentPriceYearly?.subscriptionPrice / 12
            : undefined,
    );

    const studioMonthlyEquivalentPriceWithCurrency = addCurrencyToPrice(
        studioCatalog ? studioCatalog?.currency : undefined,
        studioMonthlyEquivalentPriceYearly
            ? studioMonthlyEquivalentPriceYearly?.monthlyEquivalentPrice
            : undefined,
    );

    const isChromaticMS2Enabled = useFlag(FeatureFlag.ChromaticMS2);

    return (
        <_PricingCards
            header={customHeader}
            isLoading={!hasFetchedSubscription}
            isMobile={isMobile}
            showHeading={showHeading}
            showSampleCard={showSampleCard}
            showStudioCard={showStudioCard}
            showNeedMoreCredit={showNeedMoreCredit}
            samplesMonthlyEquivalentPriceWithCurrency={
                samplesMonthlyEquivalentPriceWithCurrency
            }
            studioMonthlyEquivalentPriceWithCurrency={
                studioMonthlyEquivalentPriceWithCurrency
            }
            isChromaticMS2Enabled={isChromaticMS2Enabled}
        />
    );
};

export default PricingCards;
